import { useEffect, useState } from "react";
import { Smartphone } from "lucide-react";
import { api, money } from "@/lib/api";
import { ADMIN_EXTRA as T } from "@/constants/testIds";

const shortDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });

const Stat = ({ label, value, testId }) => (
  <div className="bg-[#FDFBF7] rounded-2xl px-4 py-3">
    <p className="text-xs text-stone-500">{label}</p>
    <p data-testid={testId} className="font-heading text-xl font-bold">{value}</p>
  </div>
);

const Weeks = ({ weeks }) => {
  const max = Math.max(...weeks.map((w) => w.installs), 1);
  return (
    <div className="flex items-end gap-2 h-28">
      {weeks.map((w) => (
        <div key={w.week_start} data-testid={T.appStatsWeek(w.week_start)} title={`Week of ${w.week_start}: ${w.installs} installs, ${w.opens} app opens, ${w.orders} orders`} className="flex-1 flex flex-col items-center gap-1 h-full justify-end">
          <span className="text-[10px] text-stone-500">{w.installs || ""}</span>
          <div className="w-full rounded-t-md bg-[#E06D44] transition-[height] duration-500" style={{ height: `${Math.max((w.installs / max) * 100, w.installs ? 6 : 2)}%`, opacity: w.installs ? 1 : 0.25 }} />
          <span className="text-[10px] text-stone-400 whitespace-nowrap">{shortDate(w.week_start)}</span>
        </div>
      ))}
    </div>
  );
};

export const AdminAppStats = ({ refreshKey }) => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/app-stats", { params: { weeks: 8 } }).then(({ data }) => setS(data)).catch(() => setS(false)); }, [refreshKey]);
  if (!s) return null;
  const p = s.platforms;
  return (
    <div data-testid={T.appStats} className="bg-white border border-[#F0EBE1] rounded-[1.5rem] p-5 space-y-4">
      <div className="flex flex-wrap justify-between gap-2 items-center">
        <p className="font-heading font-bold inline-flex items-center gap-2"><Smartphone size={17} className="text-[#E06D44]" /> Home-screen app, last 8 weeks</p>
        <p data-testid={T.appStatsTotal} className="text-sm text-stone-600">{s.all_time_installs} install{s.all_time_installs === 1 ? "" : "s"} all time</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="Installs" value={s.installs} testId={T.appStatsInstalls} />
        <Stat label="App opens" value={s.opens} testId={T.appStatsOpens} />
        <Stat label="Orders from the app" value={s.order_rate === null ? s.orders : `${s.orders} (${s.order_rate}%)`} testId={T.appStatsOrders} />
        <Stat label="Sales from the app" value={money(s.sales)} testId={T.appStatsSales} />
      </div>
      <Weeks weeks={s.weeks} />
      <p className="text-xs text-stone-500">
        {s.installs ? `iPhone ${p.ios} · Android ${p.android}${p.other ? ` · Other ${p.other}` : ""}. ` : "No installs yet. Phone shoppers see the invite on their second visit. "}
        iPhone installs are counted the first time the icon is opened. The % is orders per app open.
      </p>
    </div>
  );
};
